import React, { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

const categoryLabels = {
  perencanaan: 'Perencanaan', 'pemilihan-penyedia': 'Pemilihan Penyedia',
  kontrak: 'Kontrak', pelaksanaan: 'Pelaksanaan', 'serah-terima': 'Serah Terima',
  pengaduan: 'Pengaduan', umum: 'Umum'
};
const priorityColors = { rendah: 'badge-gray', sedang: 'badge-blue', tinggi: 'badge-yellow', urgent: 'badge-red' };
const statusColors = { baru: 'badge-orange', diproses: 'badge-blue', dijawab: 'badge-green', selesai: 'badge-gray', ditutup: 'badge-gray' };
const statusLabels = { baru: '🆕 Baru', diproses: '🔄 Diproses', dijawab: '✅ Dijawab', selesai: '📋 Selesai', ditutup: '🔒 Ditutup' };

// Notification sound using Web Audio API
const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [523.25, 659.25, 783.99]; // C5, E5, G5 chord arpeggio
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, ctx.currentTime + i * 0.12);
      gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + i * 0.12 + 0.05);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + i * 0.12 + 0.3);
      osc.start(ctx.currentTime + i * 0.12);
      osc.stop(ctx.currentTime + i * 0.12 + 0.35);
    });
  } catch (e) { /* Audio not available */ }
};

const ConsultationPage = () => {
  const { user } = useAuth();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [form, setForm] = useState({ subject: '', category: 'umum', priority: 'sedang', description: '' });
  const [formFile, setFormFile] = useState(null);
  const [replyFile, setReplyFile] = useState(null);
  const [activeTab, setActiveTab] = useState('semua');
  const prevCountRef = useRef(0);
  const isFirstLoad = useRef(true);
  const socketRef = useRef(null);
  const selectedRef = useRef(selected);

  const fetchConsultations = useCallback(async () => {
    try {
      const { data } = await api.get('/consultations');
      const list = data.consultations;
      setConsultations(list);

      // Notification for new consultations (admin/expert only, skip first load)
      if ((user?.role === 'admin' || user?.role === 'expert') && !isFirstLoad.current) {
        const newCount = list.filter(c => c.status === 'baru').length;
        if (newCount > prevCountRef.current) {
          playNotificationSound();
          toast('🔔 Ada konsultasi baru!', {
            icon: '💬',
            style: { background: 'var(--accent-primary)', color: 'white', fontWeight: 600 }
          });
        }
        prevCountRef.current = newCount;
      } else {
        prevCountRef.current = list.filter(c => c.status === 'baru').length;
        isFirstLoad.current = false;
      }
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { fetchConsultations(); }, [fetchConsultations]);

  // Auto-refresh every 30 seconds for admin/expert
  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'expert') {
      const interval = setInterval(fetchConsultations, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchConsultations]);

  // Socket.IO real-time
  useEffect(() => {
    const socket = io();
    socketRef.current = socket;
    socket.on('reply_received', (data) => {
      if (data.consultation_id === selectedRef.current) {
        fetchConsultations();
        fetchDetail(data.consultation_id);
      }
    });
    return () => { socket.disconnect(); };
  }, []);

  useEffect(() => { selectedRef.current = selected; }, [selected]);

  useEffect(() => {
    if (socketRef.current?.connected && selected) {
      socketRef.current.emit('join_consultation', selected);
    }
  }, [selected]);

  const fetchDetail = async (id) => {
    try {
      const { data } = await api.get(`/consultations/${id}`);
      setDetail(data.consultation);
      setSelected(id);
    } catch (err) { toast.error('Gagal memuat detail'); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData();
      fd.append('subject', form.subject);
      fd.append('category', form.category);
      fd.append('priority', form.priority);
      fd.append('description', form.description);
      if (formFile) fd.append('attachment', formFile);
      await api.post('/consultations', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Konsultasi berhasil dibuat!');
      setShowForm(false);
      setForm({ subject: '', category: 'umum', priority: 'sedang', description: '' });
      setFormFile(null);
      fetchConsultations();
    } catch (err) { toast.error(err.response?.data?.error || 'Gagal'); }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() && !replyFile) return;
    try {
      const fd = new FormData();
      fd.append('message', replyText);
      if (replyFile) fd.append('attachment', replyFile);
      await api.post(`/consultations/${selected}/reply`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Balasan terkirim');
      setReplyText('');
      setReplyFile(null);
      fetchDetail(selected);
      fetchConsultations();
    } catch (err) { toast.error('Gagal mengirim balasan'); }
  };

  const handleClose = async (status) => {
    try {
      await api.put(`/consultations/${selected}/status`, { status });
      toast.success('Status diperbarui');
      fetchDetail(selected);
      fetchConsultations();
    } catch (err) { toast.error('Gagal'); }
  };

  // Filter consultations by tab
  const filteredConsultations = activeTab === 'semua'
    ? consultations
    : consultations.filter(c => {
        if (activeTab === 'aktif') return ['baru', 'diproses', 'dijawab'].includes(c.status);
        return c.status === activeTab;
      });

  // Group counts
  const counts = {
    semua: consultations.length,
    baru: consultations.filter(c => c.status === 'baru').length,
    aktif: consultations.filter(c => ['baru', 'diproses', 'dijawab'].includes(c.status)).length,
    selesai: consultations.filter(c => c.status === 'selesai' || c.status === 'ditutup').length,
  };

  // Status indicator dot for consultation card
  const StatusDot = ({ status }) => {
    const colors = { baru: '#f97316', diproses: '#3b82f6', dijawab: '#22c55e', selesai: '#6b7280', ditutup: '#6b7280' };
    return (
      <div style={{
        width: 10, height: 10, borderRadius: '50%', background: colors[status] || '#6b7280',
        flexShrink: 0, boxShadow: status === 'baru' ? '0 0 0 3px rgba(249,115,22,0.2)' : 'none',
        animation: status === 'baru' ? 'pulse-dot 2s infinite' : 'none'
      }} />
    );
  };

  // Detail view
  if (selected && detail) {
    return (
      <div className="fade-in">
        <button className="btn btn-ghost btn-sm" onClick={() => { setSelected(null); setDetail(null); }} style={{ marginBottom: 20 }}>
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" /></svg>
          Kembali
        </button>

        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <StatusDot status={detail.status} />
                <span className="badge badge-orange">{detail.ticket_number}</span>
                <span className={`badge ${statusColors[detail.status]}`}>{detail.status}</span>
                <span className={`badge ${priorityColors[detail.priority]}`}>{detail.priority}</span>
              </div>
              <h2 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: 4 }}>{detail.subject}</h2>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>
                Kategori: {categoryLabels[detail.category]} | Oleh: {detail.requester?.name}
                {detail.requester?.institution && ` (${detail.requester.institution})`}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {(user.role === 'admin' || user.role === 'expert') && detail.status !== 'selesai' && detail.status !== 'ditutup' && (
                <>
                  {detail.status === 'baru' && (
                    <button className="btn btn-secondary btn-sm" onClick={() => handleClose('diproses')}>
                      🔄 Mulai Proses
                    </button>
                  )}
                  <button className="btn btn-secondary btn-sm" onClick={() => handleClose('selesai')}>
                    ✅ Selesaikan
                  </button>
                </>
              )}
            </div>
          </div>
          <div style={{ marginTop: 16, padding: 16, background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', fontSize: '0.875rem', lineHeight: 1.7 }}>
            {detail.description}
            {detail.attachment && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-primary)' }}>
                <a
                  href={`/uploads/${detail.attachment}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ghost btn-sm"
                  style={{ gap: 6 }}
                >
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                  📎 Download File
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Replies */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
          {detail.replies?.map((reply) => (
            <div key={reply.id} className="card" style={{
              padding: '16px 20px',
              borderLeft: `3px solid ${reply.author?.role === 'expert' || reply.author?.role === 'admin' ? 'var(--accent-primary)' : 'var(--border-primary)'}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: reply.author?.role === 'expert' || reply.author?.role === 'admin'
                    ? 'linear-gradient(135deg, var(--orange-400), var(--orange-600))'
                    : 'var(--bg-tertiary)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: reply.author?.role === 'expert' || reply.author?.role === 'admin' ? 'white' : 'var(--text-secondary)',
                  fontWeight: 700, fontSize: '0.75rem'
                }}>
                  {reply.author?.name?.charAt(0)}
                </div>
                <div>
                  <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{reply.author?.name}</span>
                  {reply.author?.role === 'admin' && (
                    <span className="badge badge-red" style={{ marginLeft: 8, fontSize: '0.6875rem' }}>Admin</span>
                  )}
                  {reply.author?.role === 'expert' && (
                    <span className="badge badge-orange" style={{ marginLeft: 8, fontSize: '0.6875rem' }}>Ahli PBJ</span>
                  )}
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                    {new Date(reply.createdAt).toLocaleString('id-ID')}
                  </div>
                </div>
              </div>
              <p style={{ fontSize: '0.875rem', lineHeight: 1.7, color: 'var(--text-primary)' }}>{reply.message}</p>
              {reply.attachment && (
                <div style={{ marginTop: 8 }}>
                  <a
                    href={`/uploads/${reply.attachment}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-ghost btn-sm"
                    style={{ gap: 6, fontSize: '0.75rem' }}
                  >
                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                    📎 {reply.attachment.split('/').pop() || 'File'}
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Reply form */}
        {detail.status !== 'selesai' && detail.status !== 'ditutup' && (
          <div className="card">
            <form onSubmit={handleReply}>
              <textarea className="form-textarea" placeholder="Tulis balasan..." value={replyText} onChange={(e) => setReplyText(e.target.value)} style={{ width: '100%', marginBottom: 8 }} />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" /></svg>
                  {replyFile ? replyFile.name : 'Lampirkan File'}
                  <input type="file" hidden onChange={(e) => setReplyFile(e.target.files[0])} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" />
                </label>
                {replyFile && (
                  <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--error)' }} onClick={() => setReplyFile(null)}>
                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    Hapus
                  </button>
                )}
              </div>
              <button type="submit" className="btn btn-primary" disabled={!replyText.trim() && !replyFile}>
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" /></svg>
                Kirim Balasan
              </button>
            </form>
          </div>
        )}
      </div>
    );
  }

  // Tab data
  const tabs = (user?.role === 'admin' || user?.role === 'expert')
    ? [
        { key: 'semua', label: 'Semua', count: counts.semua },
        { key: 'baru', label: '🆕 Baru', count: counts.baru },
        { key: 'aktif', label: '🔄 Aktif', count: counts.aktif },
        { key: 'selesai', label: '✅ Selesai', count: counts.selesai },
      ]
    : [
        { key: 'semua', label: 'Semua', count: counts.semua },
        { key: 'aktif', label: 'Aktif', count: counts.aktif },
        { key: 'selesai', label: 'Selesai', count: counts.selesai },
      ];

  return (
    <div className="fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 700 }}>Daftar Konsultasi</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
          Ajukan Konsultasi
        </button>
      </div>

      {/* Status Tabs */}
      <div style={{
        display: 'flex', gap: 4, marginBottom: 20, padding: 4,
        background: 'var(--bg-secondary)', borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border-secondary)', overflowX: 'auto'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              flex: '1 0 auto', padding: '10px 16px', borderRadius: 'var(--radius-md)',
              border: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)',
              fontSize: '0.8125rem', fontWeight: activeTab === tab.key ? 700 : 500,
              background: activeTab === tab.key ? 'var(--bg-card)' : 'transparent',
              color: activeTab === tab.key ? 'var(--text-primary)' : 'var(--text-tertiary)',
              boxShadow: activeTab === tab.key ? 'var(--shadow-sm)' : 'none',
              transition: 'all var(--transition-fast)', whiteSpace: 'nowrap',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
            }}
          >
            {tab.label}
            {tab.count > 0 && (
              <span style={{
                fontSize: '0.6875rem', fontWeight: 700, padding: '1px 7px', borderRadius: 10,
                background: tab.key === 'baru' && tab.count > 0 ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
                color: tab.key === 'baru' && tab.count > 0 ? 'white' : 'var(--text-secondary)',
                minWidth: 20, textAlign: 'center'
              }}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* New consultation notification banner */}
      {(user?.role === 'admin' || user?.role === 'expert') && counts.baru > 0 && activeTab !== 'baru' && (
        <div
          onClick={() => setActiveTab('baru')}
          style={{
            padding: '12px 18px', borderRadius: 'var(--radius-md)', marginBottom: 16, cursor: 'pointer',
            background: 'linear-gradient(135deg, rgba(249,115,22,0.1), rgba(249,115,22,0.05))',
            border: '1px solid rgba(249,115,22,0.25)',
            display: 'flex', alignItems: 'center', gap: 12, transition: 'all var(--transition-fast)'
          }}
        >
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'linear-gradient(135deg, #f97316, #ea580c)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontSize: '1rem', animation: 'pulse-dot 2s infinite'
          }}>🔔</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
              {counts.baru} konsultasi baru menunggu tanggapan
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Klik untuk melihat</div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-screen"><div className="spinner" /><span>Memuat...</span></div>
      ) : filteredConsultations.length === 0 ? (
        <div className="empty-state">
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" width="48" height="48"><path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" /></svg>
          <p>{activeTab === 'semua' ? 'Belum ada konsultasi' : `Tidak ada konsultasi ${activeTab}`}</p>
          {activeTab === 'semua' && <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>Ajukan Konsultasi Pertama</button>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filteredConsultations.map((c) => (
            <div
              key={c.id}
              className="card"
              style={{
                cursor: 'pointer', padding: '18px 24px',
                borderLeft: `3px solid ${
                  c.status === 'baru' ? '#f97316' :
                  c.status === 'diproses' ? '#3b82f6' :
                  c.status === 'dijawab' ? '#22c55e' : '#9ca3af'
                }`,
                transition: 'all var(--transition-fast)'
              }}
              onClick={() => fetchDetail(c.id)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <StatusDot status={c.status} />
                    <span className="badge badge-gray" style={{ fontFamily: 'var(--font-mono)', fontSize: '0.6875rem' }}>{c.ticket_number}</span>
                    <span className={`badge ${statusColors[c.status]}`}>{c.status}</span>
                    <span className={`badge ${priorityColors[c.priority]}`}>{c.priority}</span>
                  </div>
                  <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: 4 }}>{c.subject}</h3>
                  <div style={{ display: 'flex', gap: 12, fontSize: '0.8125rem', color: 'var(--text-tertiary)', flexWrap: 'wrap' }}>
                    <span>{categoryLabels[c.category]}</span>
                    <span>•</span>
                    <span>{c.requester?.name || 'Pengguna'}</span>
                    <span>•</span>
                    <span>{new Date(c.createdAt).toLocaleDateString('id-ID')}</span>
                    {c.expert && <span>• Ditangani: {c.expert.name}</span>}
                    {c.attachment && <span>📎 File</span>}
                  </div>
                </div>
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="var(--text-tertiary)" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Ajukan Konsultasi Baru</h2>
            <form onSubmit={handleCreate}>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">Subjek *</label>
                <input className="form-input" placeholder="Topik konsultasi" value={form.subject} onChange={(e) => setForm(p => ({ ...p, subject: e.target.value }))} required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div className="form-group">
                  <label className="form-label">Kategori</label>
                  <select className="form-select" value={form.category} onChange={(e) => setForm(p => ({ ...p, category: e.target.value }))}>
                    {Object.entries(categoryLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Prioritas</label>
                  <select className="form-select" value={form.priority} onChange={(e) => setForm(p => ({ ...p, priority: e.target.value }))}>
                    <option value="rendah">Rendah</option>
                    <option value="sedang">Sedang</option>
                    <option value="tinggi">Tinggi</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">Deskripsi *</label>
                <textarea className="form-textarea" placeholder="Jelaskan pertanyaan atau permasalahan Anda secara detail..." value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} required style={{ minHeight: 140 }} />
              </div>
              <div className="form-group" style={{ marginBottom: 24 }}>
                <label className="form-label">Lampiran <span style={{ color: 'var(--text-tertiary)', fontSize: '0.75rem' }}>(opsional)</span></label>
                <input type="file" className="form-input" onChange={(e) => setFormFile(e.target.files[0])} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" />
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Batal</button>
                <button type="submit" className="btn btn-primary">Kirim Konsultasi</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default ConsultationPage;
